import { useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Button, Checkbox, FormHelperText, Stack, TextField, Typography } from '@mui/material';

import { getErrInfo } from '../common/utils';
import { signUpAtom } from '@/interfaces/auth';
import { buttonBaseStyle } from '@/styles/constant';

import type { FormEvent } from 'react';
import type { FormHelperTextProps } from '@mui/material';
import type { ErrorInfo } from '../common/utils';

interface validMsgProps {
  email: string | null;
  password: string | null;
  confirmPassword: string | null;
  agree: string | null;
}

//EMAIL VALID REGX
const emailReg = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

//PASSWORD VALID
function validatePassword(pw: string) {
  if (pw.length < 6 || pw.length > 15) return false;
  if (pw.search(/[A-Z]+/) === -1) return false;
  if (pw.search(/[a-z]+/) === -1) return false;
  if (pw.search(/\d+/) === -1) return false;
  if (pw.search(/[!@#$%^&*()_+\-=[\]\\{}|;':",./<>?]+/) === -1) return false;
  return true;
}

const slotProps: { formHelperText: FormHelperTextProps } = {
  formHelperText: { sx: { color: (t) => t.palette.error.main } },
};

export default function SignUpForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [valid, setValid] = useState<validMsgProps>({
    email: null,
    password: null,
    confirmPassword: null,
    agree: null,
  });

  const [{ data, mutate, status, error }] = useAtom(signUpAtom);

  useEffect(() => {
    if (data && status && status === 'success') {
      alert(t('Signup.message.accountRegisterSuccess') + '\n' + t('Signup.message.redirectToLogin'));
      navigate('/login', { replace: false });
    } else if (error && status === 'error') {
      const errInfo: ErrorInfo = getErrInfo(error);
      let errMsgKey = errInfo.headerCode ? 'Common.message.serverErrorOccurred' : 'Common.message.unknownErrorOccurred';
      switch (errInfo.bodyCode) {
        case 'EMAIL_ALREADY_EXIST':
          errMsgKey = 'Signup.message.alreadyExist';
          break;
        default:
          errMsgKey = errInfo.commonMsgKey !== null && errInfo.commonMsgKey;
          break;
      }
      alert(t(errMsgKey));
    }
  }, [data, error, navigate, status, t]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const data = new FormData(event.currentTarget);
    const target = {
      email: data.get('email').toString(),
      password: data.get('password').toString(),
      confirmPassword: data.get('confirmPassword').toString(),
      agree: data.get('agree'),
    };

    const validMsg: validMsgProps = {
      email:
        target.email === ''
          ? t('Signup.message.emailRequirements')
          : emailReg.test(target.email)
            ? null
            : t('Common.message.wrongEmailFormat'),
      password: validatePassword(target.password) ? null : t('Signup.message.passwordRequirements'),
      confirmPassword: target.password !== target.confirmPassword ? t('Signup.message.passwordNotSame') : null,
      agree: target.agree === null ? t('Signup.message.agreeRequirements') : null,
    };
    setValid(validMsg);
    if (
      validMsg.email === null &&
      validMsg.password === null &&
      validMsg.confirmPassword === null &&
      validMsg.agree === null
    ) {
      mutate({ email: target.email, password: target.password });
    }
  };

  return (
    <Box component={'form'} onSubmit={handleSubmit} flexGrow={1}>
      <Box display="flex" flexDirection="column" height="100%" justifyContent={'space-between'}>
        <Stack flexGrow={1} justifyContent={'space-evenly'}>
          <Typography fontWeight={'bold'} color="gray">
            {t('Signup.label.information')}
          </Typography>
          <TextField
            fullWidth
            name="email"
            variant="standard"
            placeholder={t('Signup.label.ID')}
            helperText={valid.email}
            slotProps={slotProps}
          />
          <TextField
            fullWidth
            type="password"
            name="password"
            variant="standard"
            placeholder={t('Signup.label.password')}
            helperText={valid.password}
            slotProps={slotProps}
          />
          <TextField
            fullWidth
            type="password"
            name="confirmPassword"
            variant="standard"
            placeholder={t('Signup.label.checkPassword')}
            helperText={valid.confirmPassword}
            slotProps={slotProps}
          />
          <Box>
            <Box display={'flex'} alignItems={'center'}>
              <Checkbox name="agree" />
              <Link
                to="https://m2cloud.notion.site/14c84d81857f80438fa4d9d4cf96f46f"
                target="_blank"
                style={{ textDecoration: 'none' }}
              >
                <Typography color="primary">{t('Signup.label.termsOfService')}</Typography>
              </Link>
              <Typography>&nbsp;/&nbsp;</Typography>
              <Link
                to="https://m2cloud.notion.site/14984d81857f80e78546dd6202e9c240"
                target="_blank"
                style={{ textDecoration: 'none' }}
              >
                <Typography color="primary">{t('Signup.label.privacyPolicy')}</Typography>
              </Link>
            </Box>
            <FormHelperText error={valid.agree !== null}>{valid.agree ?? ''}</FormHelperText>
          </Box>
        </Stack>
        <Box alignSelf={'center'}>
          <Button
            variant="contained"
            color="info"
            type="submit"
            sx={{
              ...buttonBaseStyle,
            }}
          >
            {t('Common.label.registButton')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
